import { useEffect, useState } from "react";
import axios from "axios";
import { Reveal, SectionHeading } from "./Primitives";
import { Star, ArrowUpRight, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fallback links (also returned by the backend from env).
const FALLBACK_REVIEW_URL = "https://g.page/r/CQKpbcAxL4rYEAE/review";
const FALLBACK_PROFILE_URL = "https://g.page/r/CQKpbcAxL4rYEAE";

const GoogleG = () => (
  <svg width="22" height="22" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.5 0 10.5-2.1 14.3-5.6l-6.6-5.6C29.6 34.6 26.9 36 24 36c-5.2 0-9.6-3.3-11.2-8l-6.6 5.1C9.6 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.4l6.6 5.6C41.9 35.7 44 30.3 44 24c0-1.3-.1-2.3-.4-3.5z" />
  </svg>
);

const Stars = ({ value = 0, size = 15 }) => {
  const rounded = Math.round(value);
  return (
    <span className="inline-flex gap-0.5" aria-label={`${value} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} size={size} className={i < rounded ? "fill-current text-gold" : "text-zinc-700"} />
      ))}
    </span>
  );
};

const CtaButtons = ({ reviewUrl, profileUrl }) => (
  <div className="flex flex-col sm:flex-row items-center justify-center gap-4 w-full sm:w-auto">
    <a
      href={reviewUrl}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="leave-google-review-btn"
      className="group inline-flex items-center justify-center gap-2 w-full sm:w-auto bg-gold text-black font-semibold px-8 py-4 hover:bg-[#c5a059] transition-colors duration-300"
    >
      <Star size={17} className="fill-current" />
      Leave a Google Review
      <ArrowUpRight size={17} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
    </a>
    <a
      href={profileUrl}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="view-google-profile-btn"
      className="group inline-flex items-center justify-center gap-2 w-full sm:w-auto border border-gold/50 text-gold font-semibold px-8 py-4 hover:bg-gold/10 hover:border-gold transition-colors duration-300"
    >
      View Our Google Profile
      <ExternalLink size={16} className="group-hover:translate-x-0.5 transition-transform" />
    </a>
  </div>
);

export const GoogleReviews = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/reviews`)
      .then((res) => { if (active) setData(res.data); })
      .catch(() => { if (active) setData({ reviews: [] }); });
    return () => { active = false; };
  }, []);

  const reviews = data?.reviews || [];
  const hasReviews = reviews.length > 0;
  const reviewUrl = data?.review_url || FALLBACK_REVIEW_URL;
  const profileUrl = data?.profile_url || FALLBACK_PROFILE_URL;

  return (
    <section id="reviews" className="bg-[#0a0a0a] border-y border-white/10 py-20 md:py-32">
      <div className="mx-auto max-w-[1100px] px-5 md:px-10">
        <Reveal>
          <SectionHeading
            overline="Reviews"
            title={<>Google <span className="gold-gradient italic">Reviews</span></>}
            subtitle="We value every customer's experience."
            center
          />
        </Reveal>

        {hasReviews ? (
          <Reveal delay={0.1}>
            {/* Overall rating */}
            <div className="mt-12 flex flex-col items-center text-center" data-testid="google-reviews-summary">
              <div className="flex items-center gap-3">
                <GoogleG />
                {data?.rating != null && (
                  <span className="font-display text-3xl text-white">{Number(data.rating).toFixed(1)}</span>
                )}
                <Stars value={data?.rating || 0} size={20} />
              </div>
              {data?.user_rating_count != null && (
                <p className="mt-2 text-sm text-zinc-400">
                  Based on {data.user_rating_count} Google review{data.user_rating_count === 1 ? "" : "s"}
                </p>
              )}
            </div>

            {/* Reviews grid */}
            <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="google-reviews-list">
              {reviews.map((r, i) => (
                <figure
                  key={r.google_maps_uri || `${r.author_name}-${i}`}
                  data-testid={`google-review-${i}`}
                  className="h-full border border-white/5 bg-[#121212] p-7 flex flex-col"
                >
                  <div className="flex items-center gap-3">
                    {r.author_photo ? (
                      <img src={r.author_photo} alt="" className="w-10 h-10 rounded-full object-cover" referrerPolicy="no-referrer" />
                    ) : (
                      <span className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-gold font-semibold">
                        {(r.author_name || "G").slice(0, 1)}
                      </span>
                    )}
                    <div className="min-w-0">
                      <p className="text-sm text-white truncate">{r.author_name || "Google reviewer"}</p>
                      <div className="flex items-center gap-2">
                        <Stars value={r.rating || 0} size={13} />
                        {r.relative_time && <span className="text-xs text-zinc-500">{r.relative_time}</span>}
                      </div>
                    </div>
                    <span className="ml-auto shrink-0"><GoogleG /></span>
                  </div>
                  {r.text && (
                    <blockquote className="mt-4 text-sm text-zinc-300 leading-relaxed flex-1 whitespace-pre-wrap line-clamp-6">
                      {r.text}
                    </blockquote>
                  )}
                  {r.google_maps_uri && (
                    <a
                      href={r.google_maps_uri}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-4 inline-flex items-center gap-1 text-xs text-gold hover:text-white transition-colors"
                    >
                      View on Google <ExternalLink size={12} />
                    </a>
                  )}
                </figure>
              ))}
            </div>

            <div className="mt-12 flex justify-center">
              <CtaButtons reviewUrl={reviewUrl} profileUrl={profileUrl} />
            </div>
          </Reveal>
        ) : (
          <Reveal delay={0.1}>
            <div
              data-testid="google-reviews-cta"
              className="mt-14 border border-white/10 bg-[#121212] p-8 sm:p-10 md:p-14 text-center flex flex-col items-center"
            >
              <div className="w-16 h-16 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
                <GoogleG />
              </div>
              <p className="mt-6 max-w-xl text-base md:text-lg text-zinc-300 leading-relaxed">
                Have you used Perfect Touch Detailed Cleaning &amp; Logistics Services?
                Share your experience with us on Google.
              </p>
              <div className="mt-8">
                <CtaButtons reviewUrl={reviewUrl} profileUrl={profileUrl} />
              </div>
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
};
